/**
 * View → Switch Windows: every open screen (a sheet) and every open editor (a
 * draft sheet) in one drop-down, plus "Close all screens". A WAI-ARIA menu:
 * Up / Down / Home / End move, Esc and Tab close it and Esc returns focus to
 * the button that opened it.
 */
import { X, SquareX } from 'lucide-react'
import { useEffect, useRef, type KeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import { EditorsList } from '../../nav/parts/EditorsList'
import { S, iconButton } from '../../nav/tokens'
import type { ShellNavModel } from '../../nav/useShellNav'
import { moveFocus } from './shared'

const heading = {
  padding: '8px 12px 6px', fontSize: 13, fontWeight: 600, color: S.text2, letterSpacing: '0.02em',
} as const

export function SwitchWindowsMenu({ model, onClose, onPicked }: {
  model: ShellNavModel
  /** Esc / Tab: close (the owner refocuses its button on Esc). */
  onClose: (refocus: boolean) => void
  /** Something was opened or closed from the menu. */
  onPicked: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const items = ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')
    const cur = ref.current?.querySelector<HTMLElement>('[role="menuitem"][aria-current]')
    ;(cur || items?.[0])?.focus()
  }, [])
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true); return }
    if (e.key === 'Tab') { onClose(false); return }
    const els = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])]
    if (moveFocus(els, e.key, false)) { e.preventDefault(); e.stopPropagation() }
  }
  const editorShown = !!model.activeEditor && model.activeEditor.objName === model.current?.obj_name
  return (
    <div ref={ref} role="menu" aria-label="Switch windows" onKeyDown={onKey}
      {...tw('action', 'shell-office-switch-menu')}
      style={{
        width: 340, maxHeight: '70vh', overflowY: 'auto', padding: 4, borderRadius: 10,
        background: S.raised, color: S.text, border: `1px solid ${S.border}`,
        boxShadow: '0 14px 34px rgba(0,0,0,0.24)',
      }}>
      <div role="presentation" style={heading}>Open screens ({model.openScreens.length})</div>
      {model.openScreens.length === 0 && (
        <div role="presentation" style={{ padding: '4px 12px 10px', fontSize: 14, color: S.text2 }}>No screens open.</div>
      )}
      {model.openScreens.map((item) => {
        const on = model.current?.id === item.id && !editorShown
        const path = model.pathText(item)
        return (
          <div key={item.id} role="none" style={{
            display: 'flex', alignItems: 'center', borderRadius: 8, background: on ? S.surface2 : 'transparent',
          }}>
            <button type="button" role="menuitem" className="tw-sh-item"
              aria-current={on ? 'page' : undefined}
              onClick={() => {
                if (model.current?.id === item.id && model.activeEditorId) model.minimiseEditor()
                else model.open(item)
                onPicked()
              }}
              {...tw('action', 'shell-office-switch-screen', { obj: item.obj_name, selected: on })}
              style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, minHeight: 44,
                padding: '4px 10px', border: 'none', borderRadius: 8, background: 'transparent',
                color: S.text, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
              }}>
              <NavIcon node={item} size={17} color={S.accent} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="tw-sh-clip" style={{ display: 'block', fontSize: 14.5, fontWeight: on ? 600 : 500 }}>{item.title}</span>
                {path && <span className="tw-sh-clip" style={{ display: 'block', fontSize: 13, color: S.text2 }}>{path}</span>}
              </span>
            </button>
            <button type="button" className="tw-sh-btn" tabIndex={-1}
              onClick={() => { model.closeScreen(item); onPicked() }}
              aria-label={`Close ${item.title}`} title="Close"
              {...tw('action', 'shell-office-switch-close', { obj: item.obj_name })}
              style={{ ...iconButton(32), marginRight: 4 }}>
              <X size={14} aria-hidden />
            </button>
          </div>
        )
      })}
      {model.editorsEnabled && (
        <>
          <div role="separator" style={{ height: 1, margin: '4px 6px', background: S.border }} />
          <div role="presentation" style={heading}>Open editors ({model.editors.length})</div>
          <EditorsList model={model} asMenu onPicked={onPicked}
            emptyText="No open editors. Add or edit a record, then minimise it to keep it here." />
        </>
      )}
      {model.openScreens.length > 0 && (
        <>
          <div role="separator" style={{ height: 1, margin: '4px 6px', background: S.border }} />
          <button type="button" role="menuitem" className="tw-sh-item"
            onClick={() => { model.closeAllScreens(); onPicked() }}
            {...tw('action', 'shell-office-switch-close-all')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '0 10px',
              border: 'none', borderRadius: 8, background: 'transparent', color: S.text, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 14.5, textAlign: 'left',
            }}>
            <SquareX size={16} aria-hidden color={S.text2} />
            Close all screens
          </button>
        </>
      )}
    </div>
  )
}
